/**
 * Dev-only WebRTC media diagnostics — provider-agnostic tap for the "call
 * connects but nobody hears anything" class of bug.
 *
 * Wraps `window.RTCPeerConnection` so every peer connection the Twilio or
 * Telnyx SDK creates is tracked, and while any is live logs a `[voice-diag]`
 * line every 3s with, per connection:
 *   - outbound audio: packetsSent delta + local mic audioLevel (media-source)
 *   - inbound audio: packetsReceived delta + remote audioLevel (inbound-rtp)
 * plus the state of every <audio> element in the DOM (paused/muted/stream).
 *
 * Reading the output:
 *   - mic level ~0 while talking        → capture broken (getUserMedia/device)
 *   - packetsSent not increasing        → uplink dead (ICE/DTLS)
 *   - packetsReceived not increasing    → downlink dead (far end / server)
 *   - inbound audioLevel > 0 but silence→ playout broken (element paused /
 *     autoplay blocked / wrong element)
 */

interface ITrackedPc {
    readonly pc: RTCPeerConnection;
    readonly label: string;
    lastPacketsSent: number;
    lastPacketsReceived: number;
}

const tracked: ITrackedPc[] = [];
let pollTimer: ReturnType<typeof setInterval> | null = null;
let installed = false;
let pcCounter = 0;

function audioElementStates(): Array<Record<string, unknown>> {
    return Array.from(document.querySelectorAll('audio')).map((el) => ({
        id: el.id || '(no id)',
        paused: el.paused,
        muted: el.muted,
        volume: el.volume,
        hasStream: el.srcObject !== null,
        readyState: el.readyState,
    }));
}

async function snapshot(entry: ITrackedPc): Promise<Record<string, unknown> | null> {
    if (entry.pc.connectionState === 'closed') return null;
    const report = await entry.pc.getStats();
    let packetsSent = 0;
    let packetsReceived = 0;
    let micLevel: number | null = null;
    let remoteLevel: number | null = null;
    report.forEach((stat) => {
        const s = stat as unknown as Record<string, unknown>;
        if (s['type'] === 'outbound-rtp' && s['kind'] === 'audio') {
            packetsSent += (s['packetsSent'] as number) ?? 0;
        } else if (s['type'] === 'inbound-rtp' && s['kind'] === 'audio') {
            packetsReceived += (s['packetsReceived'] as number) ?? 0;
            if (typeof s['audioLevel'] === 'number') remoteLevel = s['audioLevel'] as number;
        } else if (s['type'] === 'media-source' && s['kind'] === 'audio') {
            if (typeof s['audioLevel'] === 'number') micLevel = s['audioLevel'] as number;
        }
    });
    const sentDelta = packetsSent - entry.lastPacketsSent;
    const receivedDelta = packetsReceived - entry.lastPacketsReceived;
    entry.lastPacketsSent = packetsSent;
    entry.lastPacketsReceived = packetsReceived;
    return {
        pc: entry.label,
        state: entry.pc.connectionState,
        ice: entry.pc.iceConnectionState,
        micLevel,
        sent: packetsSent,
        sentDelta,
        received: packetsReceived,
        receivedDelta,
        remoteLevel,
    };
}

function poll(): void {
    const live = tracked.filter((t) => t.pc.connectionState !== 'closed');
    if (live.length === 0) return;
    void Promise.all(live.map(snapshot)).then((snaps) => {
        const rows = snaps.filter((s): s is Record<string, unknown> => s !== null);
        if (rows.length === 0) return;
        console.warn('[voice-diag]', JSON.stringify({ pcs: rows, audio: audioElementStates() }));
    });
}

/** Idempotent; call before any SDK creates its peer connection. Dev-only. */
export function installWebrtcDiagnostics(): void {
    if (installed || typeof window === 'undefined' || !('RTCPeerConnection' in window)) return;
    if (!import.meta.env.DEV) return;
    installed = true;

    const Native = window.RTCPeerConnection;
    const Wrapped = function (this: unknown, ...args: ConstructorParameters<typeof Native>) {
        const pc = new Native(...args);
        pcCounter += 1;
        tracked.push({ pc, label: `pc-${pcCounter}`, lastPacketsSent: 0, lastPacketsReceived: 0 });
        console.warn(`[voice-diag] RTCPeerConnection created: pc-${pcCounter}`);
        if (!pollTimer) pollTimer = setInterval(poll, 3_000);
        return pc;
    };
    Wrapped.prototype = Native.prototype;
    Object.setPrototypeOf(Wrapped, Native);
    (window as { RTCPeerConnection: unknown }).RTCPeerConnection = Wrapped;
}
